import {
    Box,
    Divider,
    DropdownContent,
    DropdownMenu,
    DropdownMenuItem,
    DropdownTrigger,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import { useFeature } from "flagged";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps, useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import Premium from "components/icons/Premium";
import { SwitchApplication } from "components/icons/SwitchApplication";
import AssetsSummaryFooter from "components/navigation/navigation-panel/AssetsSummaryFooter";
import style from "components/navigation/navigation-panel/main-navigation-panel/main-navigation-panel.scss";
import NavigationMenuIcons from "components/navigation/navigation-panel/main-navigation-panel/navigation-icons-menu/NavigationIconsMenu";
import { useMainMenuItems } from "components/navigation/navigation-panel/main-navigation-panel/navigation-icons-menu/useMainMenuItems";
import { NavigationPanelFooter } from "components/navigation/navigation-panel/navigation-panel-footer/NavigationPanelFooter";
import { detectApplicationFromPath, HOME_ROUTE, INTEGRATIONS_ROUTE } from "components/router/Routes";
import { FLAG_ASSET_MANAGEMENT, FLAG_BAM_PORTAL } from "services/feature/FeatureFlagService";
import { tenantHasFeatureLicense } from "services/tenants/tenantCookieService";
import { StoreState } from "store";
import { Application, setApplication } from "store/application";
import { resolveColor } from "utils/commonFunctions";

import testIds from "testIds.json";

interface Props {
    onSubNavigationOpenChange: (value: boolean) => void;
    isSubNavigationOpened: boolean;
    isMobileMainNavigationOpen: boolean;
    isTrialTenant?: boolean;
}

const connector = connect((state: StoreState) => ({
    user: state.userReducer.user,
    tenantDetails: state.tenantDetailsReducer.stack[state.tenantDetailsReducer.stack.length - 1],
    theme: state.themeReducer.theme,
}));

const MainNavigationPanel = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const navigationPanelRef = React.useRef<HTMLDivElement>(null);
    const navigationHeaderRef = React.useRef<HTMLDivElement>(null);
    const navigationContentRef = React.useRef<HTMLDivElement>(null);

    const dispatch = useDispatch();
    const application = useSelector((state: StoreState) => state.applicationReducer.selected);
    const history = useHistory();
    const { pathname } = useLocation();
    const isAssetManagementFlagActive = useFeature(FLAG_ASSET_MANAGEMENT);
    const isBamPortalFlagActive = useFeature(FLAG_BAM_PORTAL);
    const { bmpSupportMenuItem } = useMainMenuItems();

    function isBamAvailable() {
        const hasFeatureLicense = tenantHasFeatureLicense("FEATURE_ASSET_MANAGEMENT");
        return isAssetManagementFlagActive && hasFeatureLicense;
    }

    function isOnBam() {
        return application === Application.BAM;
    }

    React.useEffect(() => {
        const currentApplication = detectApplicationFromPath(pathname);
        if (currentApplication !== undefined && currentApplication !== application) {
            if (currentApplication === Application.BAM && !isBamAvailable()) {
                return;
            }
            // In BMP+assets mode the asset routes are embedded in BMP navigation,
            // so navigating to /assets/* should not switch the app to BAM.
            if (
                currentApplication === Application.BAM &&
                application === Application.BMP &&
                isAssetManagementFlagActive
            ) {
                return;
            }
            dispatch(setApplication(currentApplication));
        }
    }, [pathname, isAssetManagementFlagActive]);

    const handleSetApplication = (application: Application) => {
        dispatch(setApplication(application));
        if (application === Application.BAM) {
            history.push(INTEGRATIONS_ROUTE.path);
        } else {
            history.push(HOME_ROUTE.path);
        }
    };

    function displayAssetsSummary() {
        if (!isOnBam()) {
            return null;
        }
        return (
            <Box columnLayout>
                <Divider />
                <AssetsSummaryFooter minimized={!props.isSubNavigationOpened} />
            </Box>
        );
    }

    return (
        <Box
            columnLayout
            verticalAlignment="Center"
            className={classNames(style.mainNavigationPanel, {
                [style.mobileOpen]: props.isMobileMainNavigationOpen,
            })}
            ref={navigationPanelRef}
        >
            <Box verticalAlignment="Center" className={style.mainNavigationPanelHeader} ref={navigationHeaderRef}>
                {isBamPortalFlagActive && isBamAvailable() ? (
                    <DropdownMenu position={"bottom-start"}>
                        <DropdownTrigger testId={testIds.navigation.productSwitch.switchButton}>
                            <Tooltip placement={"bottom"}>
                                <TooltipContent testId={testIds.navigation.productSwitch.switchButtonTooltip}>
                                    {t("Routes.switchProductTooltip")}
                                </TooltipContent>
                                <TooltipTrigger>
                                    <SwitchApplication borderColor={resolveColor("--app-navigationPanel-iconColor")} />
                                </TooltipTrigger>
                            </Tooltip>
                        </DropdownTrigger>
                        <DropdownContent testId={testIds.navigation.productSwitch.dropdownMenu.itself}>
                            <DropdownMenuItem
                                onClick={() => handleSetApplication(Application.BMP)}
                                testId={testIds.navigation.productSwitch.dropdownMenu.bmpMenuItem}
                            >
                                <Box verticalAlignment="Center" justifyContent="SpaceBetween">
                                    {t("Routes.bmpProduct")}
                                    <Premium />
                                </Box>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => handleSetApplication(Application.BAM)}
                                testId={testIds.navigation.productSwitch.dropdownMenu.bamMenuItem}
                            >
                                {t("Routes.bamProduct")}
                            </DropdownMenuItem>
                        </DropdownContent>
                    </DropdownMenu>
                ) : null}
            </Box>

            <div className={style.mainNavigationPanelContent} ref={navigationContentRef}>
                <NavigationMenuIcons
                    onMenuItemSelect={(item) => {
                        props.onSubNavigationOpenChange(true);
                        history.push(item.mainRoute.path);
                    }}
                />
            </div>

            {displayAssetsSummary()}
            <NavigationPanelFooter
                isOnBam={isOnBam()}
                bmpSupportMenuItem={bmpSupportMenuItem}
                onMenuItemSelect={(item) => {
                    props.onSubNavigationOpenChange(true);
                    history.push(item.mainRoute.path);
                }}
                onSubNavigationOpenChange={props.onSubNavigationOpenChange}
            />
        </Box>
    );
};

const connectedMainNavigationPanel = connector(MainNavigationPanel);
export { connectedMainNavigationPanel as MainNavigationPanel };
